'use client';

import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from './AuthProvider';
import { redactPrivateEvent } from '@/lib/telemetry-privacy';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

// Initialize PostHog once (outside component to avoid re-init)
// Skip on localhost to avoid polluting production analytics
if (typeof window !== 'undefined' && POSTHOG_KEY && !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) {
  posthog.init(POSTHOG_KEY, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    capture_pageview: false, // We capture manually for Next.js App Router
    autocapture: false, // Save event quota
    persistence: 'localStorage+cookie',
    before_send: redactPrivateEvent,
    session_recording: { maskAllInputs: true, blockSelector: '.study-private' },
  });
}

/**
 * Captures $pageview on route changes.
 * Must be wrapped in Suspense because useSearchParams() can suspend.
 */
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user, loading } = useAuth();
  const userId = user && !user.is_anonymous ? user.id : undefined;
  const email = user?.email;

  useEffect(() => {
    if (!POSTHOG_KEY || !pathname || loading) return;
    // Retire identified device IDs before identifying an account.
    if (posthog.get_property('$user_id') && posthog.get_distinct_id() !== userId) posthog.reset();
    if (userId) posthog.identify(userId, { email });
    posthog.register({ analytics_identity_version: 2 });
    const url = window.origin + pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    posthog.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams, loading, userId, email]);

  return null;
}

/**
 * Wraps children with PostHog provider + pageview/identity tracking.
 * No-ops gracefully if NEXT_PUBLIC_POSTHOG_KEY is not set.
 */
export function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  // Always render the same tree to avoid server/client hydration mismatch.
  // PostHog init is skipped on localhost (above), so the provider is inert there.
  if (!POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PostHogProvider>
  );
}
